'use client';

import * as React from 'react';
import clsx from 'clsx';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { motion } from '../css-refs.effects.ts';
import {
	getPrimitiveLabelStyles,
	getMessageStyles,
	getInputWrapperStyles,
	getInputContainerStyles,
	buildInputFocusHandlers,
	buildInputAriaProps,
	getLabelId,
	getMessageId,
	openPickerOnPress,
} from './input-helpers.ts';
import type { FormFieldState } from '@federa/types/ui-form-field';
import type { InputContentBaseProps } from '@federa/types/ui-input';

// SHARED COMPONENTS

/** Shared required asterisk indicator for form labels */
const requiredIndicatorStyles: React.CSSProperties = {
	color: colors.semantic.error,
	marginLeft: spacing[1],
};

export function RequiredIndicator(): React.ReactElement {
	return <span style={requiredIndicatorStyles}>*</span>;
}

/**
 * The ONE `<label>` every SDL text control renders: the caption plus the {@link RequiredIndicator} the required flag
 * decides. Exported because the FLOATING variant (`components/inputs/InputFloating.tsx`) renders the same pair under
 * animated styles -- it differs only in which style builder it hands in and in whether it carries a label `id`, so a
 * second declaration there was two spellings of one accessibility decision.
 */
export function FieldLabel({
	htmlFor,
	labelId,
	style,
	label,
	required,
}: {
	htmlFor: string;
	labelId?: string;
	style: React.CSSProperties;
	label: string;
	required?: boolean;
}): React.ReactElement {
	return (
		<label id={labelId} htmlFor={htmlFor} style={style}>
			{label}
			{required && <RequiredIndicator />}
		</label>
	);
}

interface InputLabelProps {
	id: string;
	label: string;
	required?: boolean;
}

function InputLabel({ id, label, required }: InputLabelProps): React.ReactElement {
	return <FieldLabel htmlFor={id} labelId={getLabelId(id)} style={getPrimitiveLabelStyles()} label={label} required={required} />;
}

interface InputMessageProps {
	id: string;
	message: string;
	state: FormFieldState;
}

function InputMessage({ id, message, state }: InputMessageProps): React.ReactElement {
	return (
		<span id={getMessageId(id)} style={getMessageStyles(state)} role={state === 'error' ? 'alert' : undefined}>
			{message}
		</span>
	);
}

// INPUT CORE (shared <input> element)

interface InputCoreProps {
	inputRef: React.ForwardedRef<HTMLInputElement>;
	inputId: string;
	helperId: string;
	state: FormFieldState;
	displayHelperText?: string;
	disabled?: boolean;
	required?: boolean;
	className?: string;
	inputStyle: React.CSSProperties;
	value?: string | number | readonly string[];
	defaultValue?: string | number | readonly string[];
	setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
	/** The VARIANT's own placeholder, which only the floating one has (it uses `" "` to drive its label animation). The
	 *  DEFAULT variant passes none and the caller's own `placeholder` rides `props`; see the element below. */
	placeholder?: string;
	/** Optional onChange override (floating variant wraps to track hasValue) */
	onChangeOverride?: React.ChangeEventHandler<HTMLInputElement>;
	props: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;
}

/** Shared <input> element used by DefaultInputContent and FloatingInputContent. Internal to input-components -- not exported from the package. */
function InputCore({
	inputRef,
	inputId,
	helperId,
	state,
	displayHelperText,
	disabled,
	required,
	className,
	inputStyle,
	value,
	defaultValue,
	setIsFocused,
	placeholder,
	onChangeOverride,
	props,
}: InputCoreProps): React.ReactElement {
	const focusHandlers = buildInputFocusHandlers(setIsFocused, props);
	const ariaProps = buildInputAriaProps(state, displayHelperText, helperId);

	return (
		<input
			// AHEAD OF THE CALLER'S SPREAD, WHICH IS THE PRECEDENCE EVERY OTHER FORM PRIMITIVE ALREADY USES. `Textarea`
			// and both `CurrencyInput` variants write their computed `aria-*` before `{...props}`, so the caller's own
			// value wins; this one wrote them AFTER, so the primitive's `aria-describedby` -- `undefined` on every input
			// with no helper text of its own -- deleted whatever the caller had passed, exactly as the explicit
			// `placeholder` below used to. `buildInputAriaProps` now omits the keys it has no answer for, so an absent
			// reading cannot clobber; the ordering decides only the OVERRIDE, and it follows the house rule.
			{...ariaProps}
			{...props}
			ref={inputRef}
			id={inputId}
			disabled={disabled}
			required={required}
			// THE VARIANT'S PLACEHOLDER WINS, AND THE CALLER'S IS THE FALLBACK RATHER THAN THE CASUALTY. This read
			// `placeholder={placeholder}`, and a later JSX prop overwrites a spread one -- so on the DEFAULT variant,
			// which passes none, `placeholder: undefined` clobbered whatever `props` carried and React dropped the
			// attribute. Every `<Input placeholder="...">` on the default variant therefore rendered no placeholder at
			// all, silently, with no rendering test anywhere in the repo to notice. The floating variant's `" "` is
			// truthy and still wins, which is what its label animation depends on.
			placeholder={placeholder ?? props.placeholder}
			className={className}
			style={inputStyle}
			value={value}
			defaultValue={defaultValue}
			onFocus={focusHandlers.onFocus}
			onBlur={focusHandlers.onBlur}
			onChange={onChangeOverride ?? props.onChange}
			// A date, time, month or week box opens its picker from a press anywhere on it (`openPickerOnPress`).
			onClick={openPickerOnPress(props.onClick)}
		/>
	);
}

export { InputCore };

/**
 * The members {@link InputCore} reads STRAIGHT OFF the shared variant contract, named as a `Pick` of it rather than
 * re-listed: a field renamed on `InputContentBaseProps` is a compile error here instead of a prop that silently stops
 * being forwarded.
 */
type InputCoreContentProps = Pick<
	InputContentBaseProps,
	'inputRef' | 'inputId' | 'helperId' | 'state' | 'displayHelperText' | 'disabled' | 'required' | 'className' | 'value' | 'defaultValue' | 'setIsFocused' | 'props'
>;

/**
 * {@link InputCore} bound to the shared variant contract: the twelve identity/state/value members ride the bag, and
 * only the per-variant CHROME stays an argument.
 *
 * BOTH `Input` VARIANTS SPELLED THOSE TWELVE FORWARDS BY HAND, which made `InputContentBaseProps` half an SSOT --
 * the type of the shared half was declared once and the FORWARDING of it twice, so a member added to the contract
 * reached the element only in whichever variant the author remembered, and the other variant kept compiling. The
 * three arguments below are the genuine per-variant differences: the resolved input style, the floating variant's
 * `" "` placeholder that drives its label animation, and its change interceptor.
 */
export function ContentInputCore(args: {
	content: InputCoreContentProps;
	inputStyle: React.CSSProperties;
	placeholder?: string;
	onChangeOverride?: React.ChangeEventHandler<HTMLInputElement>;
}): React.ReactElement {
	return (
		<InputCore
			{...args.content}
			inputStyle={args.inputStyle}
			placeholder={args.placeholder}
			onChangeOverride={args.onChangeOverride}
		/>
	);
}

interface FloatingLabelProps {
	id: string;
	label: string;
	hasValue: boolean;
	required?: boolean;
	/** Left position class (e.g., 'left-4' or 'left-9' for currency symbol offset) */
	leftClass?: string;
}

/** Floating label for pill-shaped input variants; animates from placeholder position to top when the input has value or focus. */
function FloatingLabel({ id, label, hasValue, required, leftClass = 'left-4' }: FloatingLabelProps): React.ReactElement {
	return (
		<label
			id={getLabelId(id)}
			htmlFor={id}
			className={clsx(
				'pointer-events-none absolute inline-flex rounded px-1',
				leftClass,
				hasValue
					? 'top-1.5 translate-y-0 bg-background text-2xs font-medium tracking-wide text-muted-foreground'
					: 'top-1/2 -translate-y-1/2 rounded-none bg-transparent px-0 text-base font-normal tracking-normal text-secondary',
				'peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:rounded peer-focus:bg-background peer-focus:px-1 peer-focus:text-2xs peer-focus:font-medium peer-focus:tracking-wide peer-focus:text-primary',
			)}
			style={{
				lineHeight: typography.lineHeight.none,
				transition: `all ${motion.duration.medium} ${motion.easing.standard}`,
			}}
		>
			{label}
			{required && (
				<span className="ml-1" style={{ color: colors.semantic.error }}>
					*
				</span>
			)}
		</label>
	);
}

interface DefaultInputWrapperProps {
	id: string;
	fullWidth?: boolean;
	className?: string;
	label?: string;
	required?: boolean;
	message?: string;
	state: FormFieldState;
	/** Content rendered before the input (e.g., icon, currency symbol) */
	prefixContent?: React.ReactNode;
	/** Content rendered after the input (e.g., icon) */
	suffixContent?: React.ReactNode;
	children: React.ReactNode;
}

export function DefaultInputWrapper({
	id,
	fullWidth,
	className,
	label,
	required,
	message,
	state,
	prefixContent,
	suffixContent,
	children,
}: DefaultInputWrapperProps): React.ReactElement {
	return (
		<div style={getInputWrapperStyles(fullWidth ?? false)} className={className}>
			{label && <InputLabel id={id} label={label} required={required} />}

			<div style={getInputContainerStyles()}>
				{prefixContent}
				{children}
				{suffixContent}
			</div>

			<MessageSlot id={id} message={message} state={state} />
		</div>
	);
}

/** The message slot BOTH wrappers close with: rendered only when there is a message, so an empty one costs no node and
 *  the two variants cannot drift on which ids or state the message is wired to. */
function MessageSlot(props: { id: string; message: string | undefined; state: FormFieldState }): React.ReactElement | null {
	const { id, message, state } = props;
	return message ? <InputMessage id={id} message={message} state={state} /> : null;
}

interface FloatingInputWrapperProps {
	id: string;
	fullWidth?: boolean;
	className?: string;
	label?: string;
	hasValue: boolean;
	required?: boolean;
	labelLeftClass?: string;
	message?: string;
	state: FormFieldState;
	/** Content rendered before the input (e.g., currency symbol) */
	prefixContent?: React.ReactNode;
	children: React.ReactNode;
}

export function FloatingInputWrapper({
	id,
	fullWidth,
	className,
	label,
	hasValue,
	required,
	labelLeftClass,
	message,
	state,
	prefixContent,
	children,
}: FloatingInputWrapperProps): React.ReactElement {
	return (
		<div style={{ width: fullWidth ? '100%' : 'auto' }} className={className}>
			<div className="relative">
				<div className="relative rounded-2xl border border-muted bg-background transition focus-within:border-primary focus-within:shadow-sm">
					{prefixContent}
					{children}
					{label && <FloatingLabel id={id} label={label} hasValue={hasValue} required={required} leftClass={labelLeftClass} />}
				</div>
			</div>
			<MessageSlot id={id} message={message} state={state} />
		</div>
	);
}
